/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Signal, signal} from '@angular/core';

/**
 * Transitional view-state store for legacy controller-style setup helpers.
 *
 * Writes update the draft immediately so imperative setup code can read its
 * own changes. Angular receives one coalesced snapshot on the next task, which
 * prevents templates from observing partially applied async hydration.
 */
export class TemplateViewState<T extends object> {
  private draft: T;
  private readonly renderedState;
  private publishTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  readonly rendered: Signal<T>;

  constructor(initial: T) {
    this.draft = {...initial};
    this.renderedState = signal<T>({...initial});
    this.rendered = this.renderedState.asReadonly();
  }

  read<K extends keyof T>(key: K): T[K] {
    return this.draft[key];
  }

  write<K extends keyof T>(key: K, value: T[K]): void {
    this.draft = {...this.draft, [key]: value};
    this.schedulePublish();
  }

  patch(patch: Partial<T>): void {
    this.draft = {...this.draft, ...patch};
    this.schedulePublish();
  }

  private schedulePublish(): void {
    if (this.publishTimer || this.destroyed) {
      return;
    }
    this.publishTimer = setTimeout(() => {
      this.publishTimer = null;
      if (this.destroyed) {
        return;
      }
      this.renderedState.set({...this.draft});
    }, 0);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.publishTimer) {
      clearTimeout(this.publishTimer);
      this.publishTimer = null;
    }
  }
}
